/**
 * ProposalsPanel
 * --------------
 * Lists all Capital Project Quotes (co-branded contracted quotes for large
 * project engagements) with New / Edit / Preview / PDF / Email / Delete.
 * Mounted in the Orders tab AND Project Management tab so ops can find the
 * same asset from either workflow.
 *
 * Data source: /api/admin/capital-quotes (Mongo-backed).
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { FileText, ExternalLink, Download, Mail, Calendar, Users, Plus, Pencil, Trash2, FileType, Rocket } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;
const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function ProposalsPanel({ token }) {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/admin/capital-quotes`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setQuotes(r.data?.quotes || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const convertToProject = async (q) => {
    if (!window.confirm(`Create a new project for fulfillment from "${q.title || q.quote_number}"?`)) return;
    try {
      const r = await axios.post(`${API}/api/admin/projects/from-quote`,
        { source_type: "capital_quote", source_id: q.id },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Project created for fulfillment", { description: r.data?.project?.name });
      navigate("/admin/projects");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not convert";
      if (e?.response?.status === 409 && window.confirm(`${msg}\n\nCreate another project anyway?`)) {
        try {
          await axios.post(`${API}/api/admin/projects/from-quote`,
            { source_type: "capital_quote", source_id: q.id, allow_duplicate: true },
            { headers: { Authorization: `Bearer ${token}` } });
          toast.success("Project created");
          navigate("/admin/projects");
        } catch { toast.error("Could not convert"); }
      } else if (e?.response?.status !== 409) { toast.error(msg); }
    }
  };

  const openNew = () => navigate("/admin/proposals/quote/new");
  const openEdit = (id) => navigate(`/admin/proposals/quote/${id}`);
  const removeQuote = async (id) => {
    if (!window.confirm("Delete this capital quote? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/api/admin/capital-quotes/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Capital quote deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to delete"); }
  };

  return (
    <section className="rounded-xl border border-slate-200 bg-white shadow-sm p-5 mb-6" data-testid="proposals-panel">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-cyan-50 ring-1 ring-cyan-500/30 flex items-center justify-center">
            <FileText className="w-4 h-4 text-cyan-700" />
          </div>
          <div>
            <h3 className="text-sm font-black uppercase tracking-wider text-slate-900">Capital Project Quotes</h3>
            <p className="text-xs text-slate-500">Large-project contracted quotes — co-branded, PDF-ready, emailable</p>
          </div>
        </div>
        <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700" onClick={openNew} data-testid="proposals-new-btn">
          <Plus className="w-4 h-4 mr-1" /> New Capital Quote
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-slate-400">Loading capital quotes…</p>
      ) : quotes.length === 0 ? (
        <div className="text-center py-8 text-slate-500 text-sm">
          No capital quotes yet. Click <strong className="text-slate-700">New Capital Quote</strong> to build one.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {quotes.map((p) => (
            <div
              key={p.id}
              className="rounded-lg border border-slate-200 bg-slate-50 hover:bg-white hover:border-cyan-400 transition-colors p-4"
              data-testid={`proposal-card-${p.id}`}
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0">
                  <p className="text-[10px] font-black uppercase tracking-wider text-cyan-700">{p.quote_number}</p>
                  <h4 className="text-sm font-extrabold text-slate-900 leading-snug">{p.title}</h4>
                  <p className="text-xs text-slate-600 mt-0.5">{p.subtitle}</p>
                </div>
                <span className={`shrink-0 text-[9px] font-black uppercase tracking-wider px-2 py-1 rounded ${
                  p.status === "sent" ? "bg-emerald-100 text-emerald-700 ring-1 ring-emerald-500/30" :
                  p.status === "accepted" ? "bg-amber-100 text-amber-800 ring-1 ring-amber-500/40" :
                  p.status === "declined" ? "bg-red-100 text-red-700 ring-1 ring-red-500/30" :
                  "bg-slate-200 text-slate-700"
                }`}>
                  {p.status}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-2 mb-3 text-[11px] text-slate-600">
                <div className="inline-flex items-center gap-1.5 truncate"><Users className="w-3 h-3 text-slate-400 shrink-0" /> {p.partner_brand || "Sterile Mate"}</div>
                <div className="inline-flex items-center gap-1.5"><Calendar className="w-3 h-3 text-slate-400 shrink-0" /> Complete: {p.target_complete || "TBD"}</div>
              </div>

              <div className="flex items-center justify-between border-t border-slate-200 pt-2 mt-2 gap-2">
                <div>
                  <p className="text-[10px] text-slate-500">Contract Price</p>
                  <p className="text-base font-black text-slate-900">{fmt$(p.amount_total)}</p>
                  {p.bundle_discount ? <p className="text-[10px] text-emerald-700 font-semibold">Includes {fmt$(p.bundle_discount)} bundle discount</p> : null}
                </div>
                <div className="flex items-center gap-1 flex-wrap justify-end">
                  <button
                    onClick={() => openEdit(p.id)}
                    className="inline-flex items-center gap-1 px-2 py-1.5 rounded-md ring-1 ring-slate-300 hover:ring-cyan-500 text-xs font-bold text-slate-700 hover:text-cyan-700"
                    data-testid={`proposal-edit-${p.id}`}
                    title="Edit"
                  ><Pencil className="w-3.5 h-3.5" /></button>
                  <a
                    href={`${API}${p.preview_url}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 px-2 py-1.5 rounded-md ring-1 ring-slate-300 hover:ring-cyan-500 text-xs font-bold text-slate-700 hover:text-cyan-700"
                    data-testid={`proposal-preview-${p.id}`}
                    title="Preview"
                  ><ExternalLink className="w-3.5 h-3.5" /></a>
                  <a
                    href={`${API}${p.pdf_url}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-cyan-600 hover:bg-cyan-700 text-white text-xs font-black"
                    data-testid={`proposal-pdf-${p.id}`}
                    title="Download PDF"
                  ><Download className="w-3.5 h-3.5" /> PDF</a>
                  <a
                    href={`${API}${p.docx_url || `/api/capital-quotes/${p.id}/docx`}`}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md ring-1 ring-blue-400 bg-blue-50 hover:bg-blue-100 text-blue-800 text-xs font-black"
                    data-testid={`proposal-docx-${p.id}`}
                    title="Download as MS Word (.docx)"
                  ><FileType className="w-3.5 h-3.5" /> Word</a>
                  <button
                    onClick={() => convertToProject(p)}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-black"
                    title="Convert to project for fulfillment"
                    data-testid={`proposal-convert-${p.id}`}
                  ><Rocket className="w-3.5 h-3.5" /> Project</button>
                  <a
                    href={`mailto:?subject=${encodeURIComponent(`Sterile Mate · ${p.title} · Quote ${p.quote_number}`)}&body=${encodeURIComponent(`Please find our contracted quote:\n\n${API}${p.preview_url}\n\nRegards,\nGeorge Broughton III\nSterile Mate LLC`)}`}
                    className="inline-flex items-center gap-1 px-2 py-1.5 rounded-md ring-1 ring-slate-300 hover:ring-amber-500 text-xs font-bold text-slate-700 hover:text-amber-700"
                    data-testid={`proposal-email-${p.id}`}
                    title="Draft an email"
                  ><Mail className="w-3.5 h-3.5" /></a>
                  <button
                    onClick={() => removeQuote(p.id)}
                    className="inline-flex items-center gap-1 px-2 py-1.5 rounded-md ring-1 ring-slate-300 hover:ring-red-500 text-xs font-bold text-slate-700 hover:text-red-700"
                    data-testid={`proposal-delete-${p.id}`}
                    title="Delete"
                  ><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}


    </section>
  );
}
